"use client";

import React, { useState } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import Header from '@/components/Header';
import Footer from '@/components/Footer';

interface CartItem {
  id: string;
  name: string;
  price: number;
  quantity: number;
  imageSrc: string;
  imageAlt: string;
}

const defaultCartItems: CartItem[] = [
{
  id: '1',
  name: 'The Apalach',
  price: 16.0,
  quantity: 1,
  imageSrc: "https://img.rocket.new/generatedImages/rocket_gen_img_121598077-1776568942960.png",
  imageAlt: 'The Apalach coffee bag'
},
{
  id: '5',
  name: 'Bayside Reserve',
  price: 16.0,
  quantity: 1,
  imageSrc: "https://images.unsplash.com/photo-1661669037570-72efa689b68d",
  imageAlt: 'Bayside Reserve coffee bag'
}];


export default function CheckoutPage() {
  const [items, setItems] = useState<CartItem[]>(defaultCartItems);
  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    address: '',
    city: '',
    state: '',
    zip: '',
    cardNumber: '',
    expiry: '',
    cvv: ''
  });
  const [orderPlaced, setOrderPlaced] = useState(false);

  const updateQty = (id: string, delta: number) => {
    setItems((prev) =>
    prev.
    map((item) => item.id === id ? { ...item, quantity: Math.max(0, item.quantity + delta) } : item).
    filter((item) => item.quantity > 0)
    );
  };

  const subtotal = items.reduce((sum, item) => sum + item.price * item.quantity, 0);
  const shipping = subtotal > 0 ? 5.99 : 0;
  const total = subtotal + shipping;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setOrderPlaced(true);
  };

  if (orderPlaced) {
    return (
      <div className="page-container">
        <Header allLinksTo="/" />
        <main className="checkout-main">
          <div className="page-width">
            <div className="success-box">
              <div className="success-icon">✓</div>
              <h1 className="success-title">Order Placed!</h1>
              <p className="success-msg">
                Thank you, {form.firstName || 'friend'}! Your order has been received.<br />
                A confirmation will be sent to {form.email || 'your email'}.
              </p>
              <Link href="/" className="back-btn">Continue Shopping</Link>
            </div>
          </div>
        </main>
        <Footer />
        <style jsx>{`
          .page-container { font-family: 'Work Sans', sans-serif; color: #3d4246; background: #fff; }
          .checkout-main { padding: 60px 0; min-height: 60vh; }
          .page-width { max-width: 1100px; margin: 0 auto; padding: 0 30px; }
          .success-box { text-align: center; padding: 60px 20px; }
          .success-icon { width: 64px; height: 64px; border-radius: 50%; background: #557b97; color: #fff; font-size: 32px; display: flex; align-items: center; justify-content: center; margin: 0 auto 24px; }
          .success-title { font-size: 28px; font-weight: 600; color: #3d4246; margin: 0 0 12px; }
          .success-msg { font-size: 16px; color: #61696f; line-height: 1.6; margin: 0 0 32px; }
          .back-btn { display: inline-block; padding: 12px 28px; background: #557b97; color: #fff; text-decoration: none; font-size: 15px; font-weight: 500; letter-spacing: 0.04em; text-transform: uppercase; transition: background 0.2s; }
          .back-btn:hover { background: #3d4246; }
        `}</style>
      </div>);

  }

  return (
    <div className="page-container">
      <Header allLinksTo="/" />
      <main className="checkout-main">
        <div className="page-width">
          <div className="breadcrumb">
            <Link href="/" className="breadcrumb-link">Home</Link>
            <span className="breadcrumb-sep"> / </span>
            <span>Checkout</span>
          </div>

          <h1 className="page-title">Checkout</h1>

          <div className="checkout-layout">
            {/* Left — Form */}
            <div className="checkout-form-col">
              <form onSubmit={handleSubmit}>
                {/* Contact */}
                <section className="form-section">
                  <h2 className="section-heading">Contact Information</h2>
                  <div className="field-row">
                    <div className="field">
                      <label className="field-label">First Name</label>
                      <input
                        className="field-input"
                        type="text"
                        placeholder="Jane"
                        required
                        value={form.firstName}
                        onChange={(e) => setForm({ ...form, firstName: e.target.value })} />
                      
                    </div>
                    <div className="field">
                      <label className="field-label">Last Name</label>
                      <input
                        className="field-input"
                        type="text"
                        placeholder="Doe"
                        required
                        value={form.lastName}
                        onChange={(e) => setForm({ ...form, lastName: e.target.value })} />
                      
                    </div>
                  </div>
                  <div className="field">
                    <label className="field-label">Email Address</label>
                    <input
                      className="field-input"
                      type="email"
                      placeholder="jane@example.com"
                      required
                      value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })} />
                    
                  </div>
                </section>

                {/* Shipping */}
                <section className="form-section">
                  <h2 className="section-heading">Shipping Address</h2>
                  <div className="field">
                    <label className="field-label">Street Address</label>
                    <input
                      className="field-input"
                      type="text"
                      placeholder="123 Bay Street"
                      required
                      value={form.address}
                      onChange={(e) => setForm({ ...form, address: e.target.value })} />
                    
                  </div>
                  <div className="field-row">
                    <div className="field field--grow">
                      <label className="field-label">City</label>
                      <input
                        className="field-input"
                        type="text"
                        placeholder="Eastpoint"
                        required
                        value={form.city}
                        onChange={(e) => setForm({ ...form, city: e.target.value })} />
                      
                    </div>
                    <div className="field field--sm">
                      <label className="field-label">State</label>
                      <input
                        className="field-input"
                        type="text"
                        placeholder="FL"
                        maxLength={2}
                        required
                        value={form.state}
                        onChange={(e) => setForm({ ...form, state: e.target.value })} />
                      
                    </div>
                    <div className="field field--sm">
                      <label className="field-label">ZIP</label>
                      <input
                        className="field-input"
                        type="text"
                        placeholder="32328"
                        maxLength={10}
                        required
                        value={form.zip}
                        onChange={(e) => setForm({ ...form, zip: e.target.value })} />
                      
                    </div>
                  </div>
                </section>

                {/* Payment */}
                <section className="form-section">
                  <h2 className="section-heading">Payment</h2>

                  <div className="field">
                    <label className="field-label">Card Number</label>
                    <input
                      className="field-input"
                      type="text"
                      placeholder="1234 5678 9012 3456"
                      maxLength={19}
                      required
                      value={form.cardNumber}
                      onChange={(e) => setForm({ ...form, cardNumber: e.target.value })} />
                    
                  </div>
                  <div className="field-row">
                    <div className="field">
                      <label className="field-label">Expiry Date</label>
                      <input
                        className="field-input"
                        type="text"
                        placeholder="MM / YY"
                        maxLength={7}
                        required
                        value={form.expiry}
                        onChange={(e) => setForm({ ...form, expiry: e.target.value })} />
                      
                    </div>
                    <div className="field">
                      <label className="field-label">CVV</label>
                      <input
                        className="field-input"
                        type="text"
                        placeholder="123"
                        maxLength={4}
                        required
                        value={form.cvv}
                        onChange={(e) => setForm({ ...form, cvv: e.target.value })} />
                      
                    </div>
                  </div>
                </section>

                <button type="submit" className="place-order-btn" disabled={items.length === 0}>
                  Place Order — ${total.toFixed(2)}
                </button>
              </form>
            </div>

            {/* Right — Order Summary */}
            <div className="order-summary-col">
              <div className="summary-box">
                <h2 className="summary-heading">Order Summary</h2>

                {items.length === 0 ?
                <p className="empty-cart">Your cart is empty. <Link href="/" className="shop-link">Shop now →</Link></p> :

                <ul className="cart-items">
                    {items.map((item) =>
                  <li key={item.id} className="cart-item">
                        <div className="cart-item__img-wrap">
                          <Image
                        src={item.imageSrc}
                        alt={item.imageAlt}
                        fill
                        style={{ objectFit: 'contain' }}
                        sizes="80px" />
                      
                        </div>
                        <div className="cart-item__info">
                          <p className="cart-item__name">{item.name}</p>
                          <p className="cart-item__price">${(item.price * item.quantity).toFixed(2)}</p>
                          <div className="qty-control">
                            <button type="button" className="qty-btn" onClick={() => updateQty(item.id, -1)} aria-label="Decrease quantity">−</button>
                            <span className="qty-value">{item.quantity}</span>
                            <button type="button" className="qty-btn" onClick={() => updateQty(item.id, 1)} aria-label="Increase quantity">+</button>
                          </div>
                        </div>
                      </li>
                  )}
                  </ul>
                }

                <div className="summary-divider" />
                <div className="summary-row">
                  <span>Subtotal</span>
                  <span>${subtotal.toFixed(2)}</span>
                </div>
                <div className="summary-row">
                  <span>Shipping</span>
                  <span>{subtotal > 0 ? `$${shipping.toFixed(2)}` : '—'}</span>
                </div>
                <div className="summary-divider" />
                <div className="summary-row summary-row--total">
                  <span>Total</span>
                  <span>${total.toFixed(2)}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
      <Footer />

      <style jsx>{`
        .page-container { font-family: 'Work Sans', sans-serif; color: #3d4246; background: #fff; }
        .checkout-main { padding: 40px 0 80px; }
        .page-width { max-width: 1100px; margin: 0 auto; padding: 0 30px; }
        .breadcrumb { font-size: 13px; color: #61696f; margin-bottom: 20px; }
        .breadcrumb-link { color: #557b97; text-decoration: none; }
        .breadcrumb-link:hover { text-decoration: underline; }
        .breadcrumb-sep { margin: 0 6px; }
        .page-title { font-size: 26px; font-weight: 600; color: #3d4246; margin: 0 0 32px; text-transform: uppercase; letter-spacing: 0.04em; }
        .checkout-layout { display: grid; grid-template-columns: 1fr 380px; gap: 40px; align-items: start; }
        .form-section { margin-bottom: 32px; }
        .section-heading { font-size: 16px; font-weight: 600; color: #3d4246; text-transform: uppercase; letter-spacing: 0.05em; margin: 0 0 16px; padding-bottom: 10px; border-bottom: 1px solid #e8e9eb; }
        .field { display: flex; flex-direction: column; gap: 6px; margin-bottom: 14px; }
        .field--grow { flex: 2; }
        .field--sm { flex: 1; }
        .field-row { display: flex; gap: 14px; }
        .field-label { font-size: 13px; font-weight: 500; color: #3d4246; text-transform: uppercase; letter-spacing: 0.04em; }
        .field-input { padding: 10px 14px; border: 1px solid #dcdcdc; font-size: 15px; font-family: 'Work Sans', sans-serif; color: #3d4246; background: #fff; outline: none; transition: border-color 0.2s; }
        .field-input:focus { border-color: #557b97; }
        .card-notice { display: flex; align-items: center; gap: 8px; font-size: 13px; color: #557b97; background: #f0f5f9; padding: 10px 14px; margin-bottom: 16px; }
        .place-order-btn { width: 100%; padding: 14px; background: #557b97; color: #fff; border: none; cursor: pointer; font-size: 16px; font-family: 'Work Sans', sans-serif; font-weight: 600; letter-spacing: 0.05em; text-transform: uppercase; transition: background 0.2s; }
        .place-order-btn:hover:not(:disabled) { background: #3d4246; }
        .place-order-btn:disabled { opacity: 0.5; cursor: not-allowed; }
        .summary-box { background: #f9f9f9; border: 1px solid #e8e9eb; padding: 28px; position: sticky; top: 100px; }
        .summary-heading { font-size: 16px; font-weight: 600; color: #3d4246; text-transform: uppercase; letter-spacing: 0.05em; margin: 0 0 20px; }
        .cart-items { list-style: none; margin: 0 0 16px; padding: 0; display: flex; flex-direction: column; gap: 16px; }
        .cart-item { display: flex; gap: 14px; align-items: flex-start; }
        .cart-item__img-wrap { position: relative; width: 72px; height: 72px; flex-shrink: 0; background: #f0f0f0; }
        .cart-item__info { flex: 1; }
        .cart-item__name { font-size: 14px; font-weight: 500; color: #3d4246; margin: 0 0 4px; }
        .cart-item__price { font-size: 14px; color: #557b97; font-weight: 600; margin: 0 0 8px; }
        .qty-control { display: flex; align-items: center; gap: 8px; }
        .qty-btn { width: 26px; height: 26px; border: 1px solid #dcdcdc; background: #fff; cursor: pointer; font-size: 16px; color: #3d4246; display: flex; align-items: center; justify-content: center; transition: background 0.2s; }
        .qty-btn:hover { background: #557b97; color: #fff; border-color: #557b97; }
        .qty-value { font-size: 14px; font-weight: 500; min-width: 20px; text-align: center; }
        .summary-divider { border: none; border-top: 1px solid #e8e9eb; margin: 14px 0; }
        .summary-row { display: flex; justify-content: space-between; font-size: 15px; color: #3d4246; margin-bottom: 8px; }
        .summary-row--total { font-weight: 700; font-size: 17px; margin-bottom: 0; }
        .empty-cart { font-size: 15px; color: #61696f; }
        .shop-link { color: #557b97; text-decoration: none; }
        .shop-link:hover { text-decoration: underline; }
        @media (max-width: 860px) {
          .checkout-layout { grid-template-columns: 1fr; }
          .summary-box { position: static; }
          .field-row { flex-wrap: wrap; }
        }
        @media (max-width: 480px) {
          .page-title { font-size: 20px; }
        }
      `}</style>
    </div>);

}